import React, {useEffect, useState} from 'react';
import {makeStyles} from '@mui/styles';
import {
  Grid,
  IconButton,
  ImageList,
  ImageListItem,
  Typography,
} from '@mui/material';
import useScreenWidth from '../../../hooks/useScreenWidth';
import {Height} from '@mui/icons-material';
import ImageLightbox from './ImageLightbox';
import {Skeleton} from '@mui/material';
import {isVideo} from '../../../util/VideoHelpers';

const useStyles = makeStyles(() => ({
  imageListItem: {
    padding: window.innerWidth > 600 ? '8px !important' : '4px !important',

    '& .MuiImageListItem-item': {
      boxShadow: '0px 3px 5px rgba(0, 0, 0, 0.21)',
    },
  },
  overlay: {
    cursor: 'pointer',
    height: '100%',
    position: 'absolute',
    top: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.66)',
  },
}));

interface IProps {
  user: any;
  projects: any[];
  reviews: any[];
  loading: boolean;
}

export default function ProPastProjects({
  user,
  projects,
  reviews,
  loading,
}: IProps) {
  const classes = useStyles();
  const width = useScreenWidth();

  const projectsList = [
    ...projects,
    ...reviews.filter(
      (review: any) => review.reviewType === 'CONTRACTOR HISTORY',
    ),
  ];

  const [overlayOpen, setOverlayOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<any | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [projectsCount, setProjectsCount] = useState(0);

  useEffect(() => {
    setProjectsCount(projectsList.length);
  }, [projectsList.length]);

  const onMouseEnter = (project: any) => {
    setSelectedProject(project);
    setOverlayOpen(true);
  };

  const onMouseLeave = () => {
    setOverlayOpen(false);
  };

  const skeleton = (
    <Skeleton
      variant="rectangular"
      width={width > 600 ? '32.5%' : '49%'}
      height={width > 600 ? 300 : 180}
      style={{marginBottom: 16}}
    />
  );

  const renderListItem = (project: any, i: number) => {
    const url =
      project.images?.imageUrl ||
      project.images?.originalImageUrl ||
      (project.attachmentReferences && project.attachmentReferences[0].url);
    if (!url) {
      return null;
    }

    return (
      <ImageListItem
        id={`item_${i}/${projectsCount}`}
        cols={width <= 600 || i > 0 ? 1 : projectsCount === 1 ? 3 : 2}
        rows={
          width <= 600 || i > 1
            ? 1
            : projectsCount === 1
              ? 3
              : i === 0 || projectsCount > 2
                ? 2
                : 1
        }
        className={classes.imageListItem}
        onMouseOver={() => onMouseEnter(project)}
        onMouseLeave={onMouseLeave}>
        {isVideo(url) ? (
          <video
            width="100%"
            height="100%"
            src={url}
            style={{backgroundColor: 'black', objectFit: 'contain'}}>
            Sorry, your browser doesn't support embedded videos
          </video>
        ) : (
          <img src={url} />
        )}

        {selectedProject?.uid === project.uid && overlayOpen && (
          <Grid
            container
            direction="column"
            justifyContent="center"
            alignItems="center"
            className={classes.overlay}
            onClick={() => setLightboxOpen(true)}>
            <IconButton
              onClick={() => setLightboxOpen(true)}
              style={{
                transform: 'rotate(45deg)',
                position: 'absolute',
                top: 0,
                right: 0,
              }}
              size="large">
              <Height color="secondary" style={{fontSize: 36}} />
            </IconButton>
            <Typography
              color="secondary"
              align="center"
              style={{fontSize: width > 600 ? 26 : 16, padding: '8px 8px 0'}}>
              {project.title}
            </Typography>
            <Typography
              color="secondary"
              style={{fontSize: width > 600 ? 16 : 12}}>
              {project.imagesCount || project.attachmentReferences.length}{' '}
              photos
            </Typography>
          </Grid>
        )}
      </ImageListItem>
    );
  };

  return (
    <>
      <ImageList
        cols={width > 600 ? 3 : 2}
        rowHeight={width > 600 ? 300 : 180}
        style={{margin: width > 600 ? -2 : 0, padding: '16px 8px'}}>
        {projectsList.map((project, i) => renderListItem(project, i))}
      </ImageList>

      {loading && (
        <Grid container justifyContent="space-between">
          {skeleton}
          {skeleton}
          {skeleton}
          {skeleton}
          {skeleton}
          {skeleton}
        </Grid>
      )}

      {lightboxOpen && (
        <ImageLightbox
          project={selectedProject}
          handleClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
}
